/**
 * /apps/:slug/activity — chronological audit log scoped to one app.
 *
 * Surfaces who did what: deploys, role changes, secret rotations,
 * visibility toggles. Plain-language renderer for each action type;
 * raw audit metadata is folded out of view but available on click.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, apiMessage } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import {
  IconActivity,
  IconAlert,
  IconBolt,
  IconGlobe,
  IconKey,
  IconLock,
  IconPlus,
  IconRefresh,
  IconRocket,
  IconTrash,
  IconUsers,
} from '../components/Icons.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { Banner, Button, EmptyState, PageHeader, PageSkeleton } from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';

interface ActivityRow {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  metadata: string | null;
  created_at: number;
  actor_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
}

interface ActivityPayload {
  rows: ActivityRow[];
  hasMore: boolean;
}

function actorLabel(row: ActivityRow): string {
  if (row.actor_name) return row.actor_name;
  if (row.actor_email) return row.actor_email;
  return 'System';
}

interface ActionDescriptor {
  text: string;
  icon: typeof IconBolt;
  tone: 'brand' | 'muted' | 'warn' | 'danger' | 'ok';
}

function describeAction(row: ActivityRow): ActionDescriptor {
  let meta: Record<string, unknown> = {};
  if (row.metadata) {
    try {
      meta = JSON.parse(row.metadata) as Record<string, unknown>;
    } catch {
      // ignore — fall back to raw action below
    }
  }
  const slug = (meta['slug'] as string | undefined) ?? '';
  switch (row.action) {
    case 'app.create':
      return { text: 'created the app', icon: IconRocket, tone: 'brand' };
    case 'app.delete':
      return {
        text: 'deleted the app (soft-delete; restorable for 30 days)',
        icon: IconTrash,
        tone: 'danger',
      };
    case 'app.update':
      return { text: "updated the app's metadata", icon: IconBolt, tone: 'muted' };
    case 'app.visibility': {
      const to = meta['to'];
      return to === 'workspace'
        ? { text: 'made the app visible to anyone at Mytra', icon: IconGlobe, tone: 'brand' }
        : { text: 'made the app private', icon: IconLock, tone: 'muted' };
    }
    case 'role.grant': {
      const email = meta['target_email'] ?? '?';
      const role = meta['role'] ?? '?';
      return { text: `granted ${email} the ${role} role`, icon: IconUsers, tone: 'brand' };
    }
    case 'role.revoke': {
      const email = meta['target_email'] ?? '?';
      return { text: `revoked ${email}'s role`, icon: IconUsers, tone: 'danger' };
    }
    case 'secret.create':
      return { text: `added secret ${meta['name'] ?? '?'}`, icon: IconKey, tone: 'brand' };
    case 'secret.rotate':
      return { text: `rotated secret ${meta['name'] ?? '?'}`, icon: IconRefresh, tone: 'ok' };
    case 'secret.delete':
      return { text: `deleted secret ${meta['name'] ?? '?'}`, icon: IconKey, tone: 'danger' };
    case 'dependency.add':
      return {
        text: `wired ${meta['caller_slug'] ?? slug} to call ${meta['target_slug'] ?? '?'}`,
        icon: IconPlus,
        tone: 'brand',
      };
    case 'dependency.remove':
      return {
        text: `disconnected ${meta['caller_slug'] ?? slug} from ${meta['target_slug'] ?? '?'}`,
        icon: IconTrash,
        tone: 'danger',
      };
    default:
      return { text: row.action, icon: IconBolt, tone: 'muted' };
  }
}

const TONE_RING: Record<ActionDescriptor['tone'], string> = {
  brand: 'bg-brand-soft text-brand',
  muted: 'bg-surface-2 text-muted',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  ok: 'bg-ok-soft text-ok',
};

export function Activity() {
  const { slug } = useParams();
  const { app, payload, loading, error, setPayload } = useAppPageWith<ActivityPayload>(
    slug,
    async (s) => {
      const resp = await api.get<{ activity: ActivityRow[]; hasMore: boolean }>(
        `/api/apps/${encodeURIComponent(s)}/activity?limit=50`,
      );
      return { rows: resp.activity, hasMore: resp.hasMore };
    },
  );
  // Pagination error is tracked separately so a failed "Load more" doesn't
  // unmount the already-loaded rows (which the initial-load `error` would).
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  async function loadMore() {
    if (!slug || !payload || !payload.hasMore) return;
    const last = payload.rows[payload.rows.length - 1];
    if (!last) return;
    setLoadMoreError(null);
    try {
      const resp = await api.get<{ activity: ActivityRow[]; hasMore: boolean }>(
        `/api/apps/${encodeURIComponent(slug)}/activity?limit=50&before=${last.created_at}`,
      );
      setPayload((prev) => ({ rows: [...prev.rows, ...resp.activity], hasMore: resp.hasMore }));
    } catch (err) {
      setLoadMoreError(apiMessage(err));
    }
  }

  if (loading) return <PageSkeleton rows={5} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage app={app} title="Activity" description="App audit log — managers only." />
    );
  }

  const { rows, hasMore } = payload;

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Activity"
        description="Who did what, newest first."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0">
          {rows.length === 0 ? (
            <EmptyState
              icon={<IconActivity size={18} />}
              title="No activity yet"
              description="As people deploy, change access, or rotate secrets, you'll see it here."
            />
          ) : (
            <ol className="relative space-y-3 border-l-2 border-line ml-3 pl-6">
              {rows.map((row) => {
                const desc = describeAction(row);
                const Icon = desc.icon;
                return (
                  <li key={row.id} className="relative">
                    <span
                      className={`absolute -left-[34px] top-1.5 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-bg ${TONE_RING[desc.tone]}`}
                      aria-hidden
                    >
                      <Icon size={12} />
                    </span>
                    <div className="rounded-lg border border-line bg-surface px-4 py-3 text-sm">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="min-w-0">
                          <span className="font-medium text-ink">{actorLabel(row)}</span>{' '}
                          <span className="text-muted">{desc.text}</span>
                        </span>
                        <span className="text-xs text-faint shrink-0">
                          {relTime(row.created_at)}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {hasMore && (
            <div className="text-center mt-5">
              <Button variant="secondary" size="sm" onClick={() => void loadMore()}>
                Load more
              </Button>
            </div>
          )}

          {loadMoreError && (
            <div className="mt-4">
              <Banner tone="error">
                <span className="inline-flex items-center gap-1.5">
                  <IconAlert size={14} />
                  {loadMoreError}
                </span>
              </Banner>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
